import React from 'react';
import PropTypes from 'prop-types';
import styled, { CSSObject } from '@emotion/styled';
import { css } from '@emotion/core';
import { CountryRegionData } from 'react-country-region-selector';
import CaretDown from '../icons/CaretDown';
import StatusIcon from '../atoms/icons/StatusIcon';
import { getBorders, spacing, getStatusColor, getInputTheme } from '../style/';
import InputLabel from './InputLabel';
import { getBlacklistedCountries, getPriorityCountries } from '../common';
import { isDarkMode } from '../utils/helpers';
import { IBaseSelectProps, ISelectContainer } from './types/Select';
import { CustomStyledProps } from 'style/style';

const getDropdownStyles = () => ({
  border: getBorders().DARKER,
  boxShadow: '0 1px 4px 0 rgba(0,0,0,.04)',
  padding: '0 14px',
  borderRadius: 3,
  paddingRight: 26,
  appearance: 'none',
  display: 'inline-block',
  fontSize: 14,
  lineHeight: '38px',
  cursor: 'pointer',
  background: getInputTheme().backgroundInput,
  color: getInputTheme().colorInput,
  width: '100%',
  height: 40,

  '&:focus': {
    outline: 'none',
    border: getBorders().FOCUS,
  },
});

const Dropdown = styled('select')(props => ({
  ...getDropdownStyles(),
  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  borderColor: getStatusColor()[props?.status],
  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  ...props?.customSelectStyle,
}));

const StatusDescription = styled('div')(
  {
    position: 'absolute',
    ...spacing.MARGIN_TOP_XS,
  },
  ({ status }: CustomStyledProps) => ({
    color: getStatusColor()[status],
  })
);

const Icon = styled('div')({
  position: 'absolute',
  right: 12,
  top: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  height: '100%',
  pointerEvents: 'none',
});

const DropdownContainer = styled('div')(
  (): CSSObject => ({
    position: 'relative',
    height: 40,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  }),
  ({ dropdownContainerCustomStyle }: CustomStyledProps) => dropdownContainerCustomStyle
);

const StatusIconContainer = styled('div')`
  position: absolute;
  right: -24px;
`;

const SelectContainer = styled('div')<ISelectContainer>(
  ({ noSpacing }) =>
    noSpacing
      ? {
          paddingBottom: 0,
          marginRight: 0,
        }
      : {
          paddingBottom: 24,
          marginRight: 24,
        },
  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  ({ customStyle }) => css(customStyle)
);

const renderStatusIcon = (status: string) => {
  return (
    status !== 'default' && (
      <StatusIconContainer>
        {' '}
        <StatusIcon status={status} />
      </StatusIconContainer>
    )
  );
};

const renderStatusMessage = (status: string, statusMessage: string) => {
  return (
    statusMessage && status !== 'default' && <StatusDescription status={status}>{statusMessage}</StatusDescription>
  );
};

export const BaseSelect = ({
  id,
  inputLabel,
  tooltip,
  labelDescription,
  status,
  statusMessage,
  style,
  noSpacing,
  children,
  noStatusIcon,
  isRequired = false,
  customStyleLabel = {},
  baseSelectStyle,
  customIconStyle = {},
  customContainerStyle = {},
  dropdownContainerCustomStyle = {},
}: IBaseSelectProps): JSX.Element => {
  const showStatus = !noSpacing;
  const darkMode = isDarkMode();

  return (
    <SelectContainer customStyle={baseSelectStyle ? baseSelectStyle : style} noSpacing={noSpacing}>
      {inputLabel ? (
        <InputLabel
          label={inputLabel}
          tooltip={tooltip}
          labelDescription={labelDescription}
          inputId={`label-${id}`}
          data-test-id={`label-${id}`}
          isBilling
          isRequired={isRequired}
          customStyle={customStyleLabel}
          customContainerStyle={customContainerStyle}
        />
      ) : null}
      <DropdownContainer
        dropdownContainerCustomStyle={dropdownContainerCustomStyle}
        className={darkMode ? 'darkmode' : 'lightmode'}
        data-test-id={`select-${id}`}
      >
        {children}
        <Icon style={customIconStyle}>
          <CaretDown />
        </Icon>
        {showStatus && !noStatusIcon && renderStatusIcon(status)}
      </DropdownContainer>
      {showStatus && renderStatusMessage(status, statusMessage)}
    </SelectContainer>
  );
};

interface ISelectProps {
  customSelectStyle?: React.CSSProperties;
  customDropDownStyle?: React.CSSProperties;
  [key: string]: any;
}
export const Select = ({ customDropDownStyle, ...rest }: ISelectProps): JSX.Element => {
  return (
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    <BaseSelect {...rest}>
      <Dropdown {...rest} style={{ ...rest.style, ...customDropDownStyle }}>
        {rest.children}
      </Dropdown>
    </BaseSelect>
  );
};

interface CountrySelectProps {
  status: any;
  countryDropdownType?: string;
  value: any;
  style: any;
  isBilling: any;
  onClick?: any;
  isRequired?: boolean;
  baseSelectStyle?: React.CSSProperties;
}

export const CountrySelect = ({ status, isRequired = false, ...rest }: CountrySelectProps): any => {
  const blacklist = getBlacklistedCountries();
  const priority = getPriorityCountries();

  return (
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    <BaseSelect status={status} isRequired={isRequired} {...rest}>
      <Dropdown {...rest}>
        <option>Select Country</option>
        {CountryRegionData.map((item, key) => {
          if (blacklist.indexOf(item[1]) < 0 && priority.indexOf(item[1]) > -1) {
            return (
              <option key={key} value={item[1]}>
                {item[0]}
              </option>
            );
          }
        }).reverse()}
        <option>----</option>
        {CountryRegionData.map((item, key) => {
          if (blacklist.indexOf(item[1]) < 0 && priority.indexOf(item[1]) < 0) {
            return (
              <option key={key} value={item[1]}>
                {item[0]}
              </option>
            );
          }
        })}
      </Dropdown>
    </BaseSelect>
  );
};

const baseSelectPropTypes = {
  /** Unique id for select dropdown */
  id: PropTypes.string,
  /** Label of select input */
  inputLabel: PropTypes.string,
  /** Label description */
  labelDescription: PropTypes.string,
  /** Tooltip next to label */
  tooltip: PropTypes.shape({
    icon: PropTypes.any,
    iconWidth: PropTypes.string,
    text: PropTypes.string,
    overlayStyle: PropTypes.any,
    placement: PropTypes.oneOf(['left', 'right', 'top', 'bottom', 'topLeft', 'topRight', 'bottomLeft', 'bottomRight']),
  }),
  /** Status that determines input style */
  status: PropTypes.oneOf(['default', 'success', 'error', 'caution']),
  /** Status message */
  statusMessage: PropTypes.string,
  /** Determines if status icon is visible */
  showStatusIcon: PropTypes.bool,
  /** Removes padding and margin defined to display status icons and messages - setting this to true will remove the abiity for the dropdown to display a status message/icon */
  noSpacing: PropTypes.bool,
  value: PropTypes.any,
  name: PropTypes.string,
  style: PropTypes.any,
  baseSelectStyle: PropTypes.any,
  customStyleLabel: PropTypes.any,
  isRequired: PropTypes.bool,
  noStatusIcon: PropTypes.any,
  error: PropTypes.any,
};

BaseSelect.propTypes = {
  ...baseSelectPropTypes,
};

// Prop types

Select.propTypes = {
  ...baseSelectPropTypes,
  /** Default value of dropdown */
  defaultValue: PropTypes.string,
  /** Disable dropdown */
  disabled: PropTypes.bool,
  /** Function to handle change of selection */
  onChange: PropTypes.func.isRequired,
  children: PropTypes.any,
};

CountrySelect.propTypes = {
  ...baseSelectPropTypes,
  /** Function to handle change of selection */
  onChange: PropTypes.func.isRequired,
};

// Default props

BaseSelect.defaultProps = {
  status: 'default',
  showStatusIcon: true,
};

Select.defaultProps = {
  status: 'default',
};

CountrySelect.defaultProps = {
  status: 'default',
};
